import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db, storage } from "../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { toast } from "react-hot-toast";

const MAX_MB = 15;
const ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
];

export default function PaymentWindow({ user }) {
  const { ticketId } = useParams();
  const [t, setT] = useState(null);

  const [slipUrl, setSlipUrl] = useState("");
  const [refId, setRefId] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  // fallback payment methods (if ticket snapshot didn’t include them)
  const [fallback, setFallback] = useState({
    banks: [],
    binance: "",
    country: "",
    district: "",
  });

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, "tickets", ticketId));
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() };
      setT(data);
      setSlipUrl(data.proofUrl || "");

      if (!data.agentBanks?.length && data.agentUid) {
        const a = await getDoc(doc(db, "agents", data.agentUid));
        if (a.exists()) {
          const ad = a.data() || {};
          setFallback({
            banks: Array.isArray(ad.banks) ? ad.banks : [],
            binance: ad.binancePayId || "",
            country: ad.country || "",
            district: ad.district || "",
          });
        }
      }
    })();
  }, [ticketId]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED_TYPES.includes(f.type))
      return toast.error("Only images or PDF allowed.");
    if (f.size > MAX_MB * 1024 * 1024)
      return toast.error(`File too large. Max ${MAX_MB}MB`);
    setFileObj(f);
  };

  const doUploadIfNeeded = async () => {
    if (!fileObj) return slipUrl || ""; // already have URL or none
    const ext = fileObj.name.split(".").pop()?.toLowerCase() || "dat";
    const path = `tickets/${ticketId}/proof_${Date.now()}.${ext}`;
    const r = ref(storage, path);
    const task = uploadBytesResumable(r, fileObj, {
      contentType: fileObj.type,
      cacheControl: "public,max-age=31536000",
    });
    return new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (s) =>
          setUploadPct(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
        (err) => reject(err),
        async () => {
          const url = await getDownloadURL(r);
          resolve(url);
        }
      );
    });
  };

  const handleSubmit = async () => {
    if (!t) return;
    const expAt =
      t.paymentExpireAt?.toDate?.()?.getTime?.() ||
      new Date(t.paymentExpireAt || 0).getTime();
    if (expAt && Date.now() > expAt)
      return toast.error("Time up. Your numbers were released.");
    if (!fileObj && !slipUrl && !refId)
      return toast.error("Upload a slip or enter a reference ID.");

    try {
      setSubmitting(true);
      const url = await doUploadIfNeeded(); // may return existing slipUrl or upload a new one
      await updateDoc(doc(db, "tickets", ticketId), {
        status: "payment_submitted",
        proofUrl: url || slipUrl || "",
        referenceId: refId || "",
        paymentSubmittedAt: serverTimestamp(),
      });
      toast.success("Payment submitted. Wait for agent approval.");
      navigate("/my-tickets", { replace: true });
    } catch (e) {
      console.error(e);
      toast.error("Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!t) return <div className="max-w-2xl mx-auto mt-10">Loading…</div>;

  const expMillis =
    t.paymentExpireAt?.toDate?.()?.getTime?.() ||
    new Date(t.paymentExpireAt || 0).getTime();
  const expired = expMillis && Date.now() > expMillis;

  const currencySymbol =
    t.currency === "LKR"
      ? "Rs"
      : t.currency === "USD"
      ? "$"
      : t.currency === "EUR"
      ? "€"
      : t.currency === "GBP"
      ? "£"
      : t.currency === "INR"
      ? "₹"
      : t.currency || "$";

  const banks = (t.agentBanks?.length ? t.agentBanks : fallback.banks) || [];
  const binance = t.agentBinancePayId || fallback.binance || "";
  const country = t.agentCountry || fallback.country || "";
  const district = t.agentDistrict || fallback.district || "";

  return (
    <div className="max-w-2xl mx-auto mt-8 md:mt-16 bg-slate-800 p-6 rounded-2xl">
      <h1 className="text-2xl font-bold mb-2">Complete Payment</h1>

      {expired && (
        <div className="p-3 mb-4 rounded bg-red-900/40 text-red-300">
          Time up. Your numbers were released.
        </div>
      )}
      {!expired && t.paymentExpireAt && (
        <div className="p-3 mb-4 rounded bg-yellow-900/40 text-yellow-300">
          Numbers held until: <b>{new Date(expMillis).toLocaleTimeString()}</b>
        </div>
      )}

      <div className="mb-4">
        <div className="text-sm text-slate-300">Agent</div>
        <div className="text-lg font-semibold">
          {t.agentName || t.agentUid?.slice(0, 6)}
          {(district || country) && (
            <span className="text-sm text-slate-300">
              {" "}
              · {district || "-"}, {country || "-"}
            </span>
          )}
        </div>
      </div>

      {/* All bank methods */}
      <div className="space-y-3 mb-4">
        <div className="grid gap-3">
          {banks.map((b, i) => (
            <div
              key={(b._id || i) + (b.accountNumber || i)}
              className="p-3 rounded bg-slate-700"
            >
              <div className="text-sm text-slate-300">Bank {i + 1}</div>
              <div className="font-semibold">{b.bankName}</div>
              <div className="text-sm">
                {b.accountName} · {b.accountNumber}
                {b.branch ? ` · ${b.branch}` : ""}
              </div>
            </div>
          ))}
          {!banks.length && (t.agentBankName || t.agentAccountNumber) && (
            <div className="p-3 rounded bg-slate-700">
              <div className="text-sm text-slate-300">Bank</div>
              <div className="font-semibold">{t.agentBankName || "-"}</div>
              <div className="text-sm">
                {t.agentAccountName || "-"} · {t.agentAccountNumber || "-"}
              </div>
            </div>
          )}
          {!banks.length && !(t.agentBankName || t.agentAccountNumber) && (
            <div className="p-3 rounded bg-slate-700 text-sm text-slate-300">
              No bank details provided yet. Please contact the agent.
            </div>
          )}
        </div>

        {!!binance && (
          <div className="p-3 rounded bg-slate-700">
            <div className="text-sm text-slate-300">Binance Pay ID</div>
            <div className="font-semibold">{binance}</div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          <div className="p-3 rounded bg-slate-700">
            <div className="text-sm text-slate-300">Amount</div>
            <div className="font-semibold">
              {currencySymbol}
              {Number(t.amount || 0).toFixed(2)}
            </div>
          </div>
          {t.unitPrice ? (
            <div className="p-3 rounded bg-slate-700">
              <div className="text-sm text-slate-300">Price / number</div>
              <div className="font-semibold">
                {currencySymbol}
                {Number(t.unitPrice).toFixed(2)}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Upload area (no explicit upload button) */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-slate-300">
            Proof file (image/PDF)
          </label>
          <input
            type="file"
            accept=".pdf,image/*"
            className="w-full mt-2 file:mr-3 file:px-3 file:py-2 file:rounded file:bg-slate-600 file:text-white file:border-0 file:cursor-pointer bg-slate-700 text-white p-1 rounded"
            onChange={onPickFile}
            disabled={submitting || expired}
          />
          {fileObj && (
            <div className="text-xs text-slate-300 mt-1">
              {fileObj.name} · {(fileObj.size / (1024 * 1024)).toFixed(2)} MB{" "}
              {submitting && uploadPct ? `· ${uploadPct}%` : ""}
            </div>
          )}
        </div>

        <div>
          <label className="text-sm text-slate-300">OR paste proof URL</label>
          <input
            className="w-full mt-2 px-3 py-2 rounded bg-slate-700 text-white"
            value={slipUrl}
            onChange={(e) => setSlipUrl(e.target.value)}
            placeholder="https://…"
            disabled={submitting || expired}
          />
          <div className="mt-4">
            <label className="text-sm text-slate-300">Reference/Slip ID</label>
            <input
              className="w-full mt-2 px-3 py-2 rounded bg-slate-700 text-white"
              value={refId}
              onChange={(e) => setRefId(e.target.value)}
              placeholder="Transaction reference"
              disabled={submitting || expired}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleSubmit}
          className="px-4 py-2 rounded-xl bg-lime-400 text-black font-semibold disabled:opacity-50"
          disabled={expired || submitting}
        >
          {submitting ? "Submitting…" : "Submit Payment"}
        </button>
        <Link to="/my-tickets" className="px-4 py-2 rounded-xl bg-slate-700">
          Back to Tickets
        </Link>
      </div>
    </div>
  );
}
